import { Link, useParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/common/Button';
import { Icon } from '../components/common/Icon';
import { Reveal } from '../components/animation/Reveal';
import { LoadingScreen } from '../components/common/Spinner';
import { ProductCard } from '../components/product/ProductCard';
import { FaqBlock, langLabel } from '../components/product/ProductFactPanels';
import { breadcrumbSchema, cropProgrammeSchema, faqSchema } from '../lib/seo';
import { productsForCrop } from '../lib/cropLinks';
import { buildCropProgramme, cropScheduleRows, cropFaq } from '../lib/cropProgramme';
import { articlesForCategory } from '../data/knowledge';
import { useCrop, useProductsDetailed } from '../hooks';

/**
 * One crop programme.
 *
 * The page exists to answer three questions in html a machine can read: which products are documented
 * for this crop, at what stage each is applied, and at what dose. Those rows are lifted verbatim from
 * each product's own dose table, matched on the crop names that product sheets use for this crop.
 *
 * Where a recommended product's sheet carries no row for this crop, the page SAYS SO in the gap note
 * rather than quietly omitting the product or filling the cell from a nearby row.
 */
export default function CropDetailPage() {
  const { slug = '' } = useParams();
  const { data: crop, isLoading } = useCrop(slug);
  // Detailed, not the list: the dose rows this page is built from live in each product's detail
  // sections, which the catalogue list endpoint does not carry.
  const { data: allProducts = [], isLoading: productsLoading } = useProductsDetailed();

  if (isLoading) {
    return (
      <div className="pt-[var(--nav-h)]">
        <LoadingScreen label="Loading crop" />
      </div>
    );
  }

  if (!crop) {
    return (
      <>
        <PageHeader title="Crop not found" subtitle="We don’t have a solution page for that crop yet." />
        <div className="container-x py-16">
          <Button to="/crop-solutions" variant="primary" size="lg">
            <Icon name="ArrowLeft" className="h-5 w-5" /> All crop solutions
          </Button>
        </div>
      </>
    );
  }

  const products = productsForCrop(crop, allProducts);
  // Until the detail calls land there are no dose rows to match, and saying "no row names this crop"
  // while still loading would be a claim about the data rather than about the page's state.
  const loadingProgramme = productsLoading && !products.length;
  const programme = buildCropProgramme(crop, products);
  const schedule = cropScheduleRows(programme);
  const faq = cropFaq(programme);
  const accent = products[0]?.accentColor ?? '#159A3C';
  const enquireTo = products[0] ? `/inquiry/${products[0].id}` : '/catalog';
  const categorySlugs = [...new Set(products.map((p) => p.categorySlug).filter(Boolean))];
  const articles = [...new Set(categorySlugs.flatMap((c) => articlesForCategory(c)))].slice(0, 3);
  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Crop Solutions', path: '/crop-solutions' },
    { name: crop.name, path: `/crop-solutions/${crop.slug}` },
  ];

  const answer = products.length
    ? `GreenbayBio documents ${products.length} product${products.length === 1 ? '' : 's'} for ${crop.name.toLowerCase()}: ${products
        .map((p) => p.name)
        .join(', ')}. ${
        schedule.length
          ? `The schedule below reproduces ${schedule.length} application${schedule.length === 1 ? '' : 's'} from the products’ own technical specifications, with the crop stage and dose stated for each.`
          : 'Doses for this crop follow each product’s general rate — see the individual product pages.'
      }`
    : `No GreenbayBio products are currently tagged for ${crop.name.toLowerCase()}.`;

  return (
    <>
      <Seo
        title={`${crop.name} — GreenbayBio Crop Programme, Products & Dosage`}
        description={`Products GreenbayBio documents for ${crop.name.toLowerCase()}, with the crop stage and dose stated in each product's technical specifications, application methods, precautions and data sheets.`}
        path={`/crop-solutions/${crop.slug}`}
        keywords={[
          `bio fertilizer for ${crop.name.toLowerCase()}`,
          `${crop.name.toLowerCase()} crop programme`,
          ...(crop.aliases ?? []).map((a) => `${a.toLowerCase()} bio input`),
        ]}
        jsonLd={[
          cropProgrammeSchema(
            crop,
            products.map((p) => ({ name: p.name, slug: p.slug })),
          ),
          ...(faq.length ? [faqSchema(faq)] : []),
          breadcrumbSchema(crumbs),
        ]}
      />
      <PageHeader
        eyebrow={`${crop.group ?? 'Crop'} · Crop solution`}
        title={crop.name}
        subtitle={crop.intro}
        accent={accent}
      />

      <div className="container-x py-14">
        {/* The direct answer — complete on its own, before anything that needs scrolling. */}
        <p className="max-w-3xl text-xl leading-relaxed text-ink">{answer}</p>

        {crop.note && (
          <Reveal className="mt-8 flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50/60 p-6" y={18}>
            <Icon name="Info" className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
            <div>
              <p className="font-semibold text-brand-900">Programme note</p>
              <p className="mt-1 text-brand-800">{crop.note}</p>
            </div>
          </Reveal>
        )}

        {/* ── the schedule: product × stage × dose, verbatim ── */}
        {schedule.length > 0 && (
          <section className="mt-12" aria-labelledby="schedule-heading">
            <h2 id="schedule-heading" className="font-display text-h2 font-semibold text-ink">
              Application schedule for {crop.name.toLowerCase()}
            </h2>
            <p className="mt-2 max-w-3xl text-ink-500">
              Every row is reproduced from the named product’s technical specifications. Doses are per acre
              unless the product’s own table states otherwise.
            </p>
            <figure className="m-0 mt-6">
              <div className="overflow-x-auto rounded-xl border border-sand-200">
                <table className="w-full min-w-[40rem] border-collapse text-sm">
                  <caption className="sr-only">
                    GreenbayBio application schedule for {crop.name}: product, application, crop stage and dose
                  </caption>
                  <thead>
                    <tr className="bg-sand-100/70 text-left">
                      <th scope="col" className="px-4 py-3 font-semibold text-ink-700">Product</th>
                      <th scope="col" className="px-4 py-3 font-semibold text-ink-700">Application</th>
                      <th scope="col" className="px-4 py-3 font-semibold text-ink-700">When to apply</th>
                      <th scope="col" className="px-4 py-3 font-semibold text-ink-700">Dose</th>
                      {schedule.some((r) => r.note) && (
                        <th scope="col" className="px-4 py-3 font-semibold text-ink-700">Notes</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((r, i) => (
                      <tr
                        key={`${r.productSlug}-${i}`}
                        className="border-t border-sand-200 odd:bg-white even:bg-canvas/60"
                      >
                        <th scope="row" className="px-4 py-3 text-left align-top font-medium text-ink">
                          <Link to={`/catalog/${r.productSlug}`} className="text-brand-700 hover:underline">
                            {r.productName}
                          </Link>
                        </th>
                        <td className="px-4 py-3 align-top text-ink-500">{r.application ?? '—'}</td>
                        <td className="px-4 py-3 align-top text-ink-500">{r.timing ?? '—'}</td>
                        <td className="px-4 py-3 align-top font-medium tabular-nums text-ink-700">{r.dose}</td>
                        {schedule.some((x) => x.note) && (
                          <td className="px-4 py-3 align-top text-ink-500">{r.note ?? '—'}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <figcaption className="mt-3 text-xs text-ink-500">
                Always follow the rate, timing and precautions printed on the pack label. Where soil, climate
                or field condition differs from the programme, ask a Greenbay crop advisor before changing a dose.
              </figcaption>
            </figure>
          </section>
        )}

        {loadingProgramme && (
          <p className="mt-8 text-ink-500">Loading the {crop.name.toLowerCase()} programme…</p>
        )}

        {/* Gap named, not hidden. */}
        {!productsLoading && programme.withoutCropRows.length > 0 && (
          <p className="mt-5 flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <Icon name="AlertCircle" className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>
              {programme.withoutCropRows.map((p) => p.name).join(', ')}{' '}
              {programme.withoutCropRows.length === 1 ? 'is' : 'are'} recommended for {crop.name.toLowerCase()} but
              {programme.withoutCropRows.length === 1 ? ' its' : ' their'} technical specifications list no row
              naming this crop — so no crop-specific dose is shown above. Use the general rate on{' '}
              {programme.withoutCropRows.length === 1 ? 'its' : 'each'} product page.
            </span>
          </p>
        )}

        {/* ── stages ── */}
        {programme.stages.length > 0 && (
          <section className="mt-12" aria-labelledby="stages-heading">
            <h2 id="stages-heading" className="font-display text-h3 font-semibold text-ink">
              Crop stages the programme covers
            </h2>
            <ul className="mt-4 flex flex-wrap gap-2">
              {programme.stages.map((s) => (
                <li
                  key={s}
                  className="rounded-full border border-sand-200 bg-white px-3.5 py-1.5 text-sm text-ink-700"
                >
                  {s}
                </li>
              ))}
            </ul>
          </section>
        )}

        {programme.methodsByProduct.length > 0 && (
          <section className="mt-12" aria-labelledby="methods-heading">
            <h2 id="methods-heading" className="font-display text-h3 font-semibold text-ink">
              How each product is applied
            </h2>
            {/* Per product, not a merged list: a method belongs to a product. Biovin is broadcast and
                Nature's Green 4+ is sprayed, and one pooled row of chips would imply either can be both. */}
            <dl className="mt-4 divide-y divide-sand-200 border-y border-sand-200">
              {programme.methodsByProduct.map((m) => (
                <div key={m.product.slug} className="py-3 sm:grid sm:grid-cols-[13rem_minmax(0,1fr)] sm:gap-4">
                  <dt className="text-sm font-semibold text-ink-700">
                    <Link to={`/catalog/${m.product.slug}`} className="text-brand-700 hover:underline">
                      {m.product.name}
                    </Link>
                  </dt>
                  <dd className="mt-1 text-[0.95rem] text-ink-600 sm:mt-0">{m.methods.join(' · ')}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-sm text-ink-500">
              <Link to="/knowledge/foliar-and-soil-application" className="text-brand-700 hover:underline">
                How foliar, soil and fertigation application differ
              </Link>
              .
            </p>
          </section>
        )}

        {/* ── products ── */}
        <section className="mt-14" aria-labelledby="products-heading">
          <h2 id="products-heading" className="font-display text-h2 font-semibold text-ink">
            Products documented for {crop.name.toLowerCase()}
          </h2>
          {products.length > 0 ? (
            <Reveal className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" stagger={0.1} y={26}>
              {products.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </Reveal>
          ) : (
            <p className="mt-6 rounded-xl border border-dashed border-sand-200 bg-white px-6 py-12 text-center text-ink-500">
              No products are currently tagged for this crop.
            </p>
          )}
        </section>

        {/* ── precautions ── */}
        {programme.precautions.length > 0 && (
          <section className="mt-14" aria-labelledby="precautions-heading">
            <h2 id="precautions-heading" className="font-display text-h3 font-semibold text-ink">
              Precautions across this programme
            </h2>
            <ul className="mt-4 space-y-2.5">
              {programme.precautions.map((line) => (
                <li key={line} className="flex gap-3 text-ink-700">
                  <Icon name="AlertCircle" className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
                  <span className="leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── documents ── */}
        {programme.documents.length > 0 && (
          <section className="mt-14" aria-labelledby="docs-heading">
            <h2 id="docs-heading" className="font-display text-h3 font-semibold text-ink">
              Technical documents for this programme
            </h2>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {programme.documents.map((d, i) => (
                <li key={`${d.url}-${i}`}>
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...(d.lang ? { hrefLang: d.lang } : {})}
                    className="inline-flex items-center gap-2.5 py-1.5 text-sm font-medium text-ink-700 hover:text-brand-600"
                  >
                    <Icon name="FileText" className="h-4 w-4 shrink-0 text-brand-500" />
                    <span>
                      {d.product.name} {d.label}
                      {d.lang ? <span className="text-ink-500"> · {langLabel(d.lang)}</span> : null}
                      <span className="text-ink-400"> (PDF)</span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <FaqBlock items={faq} heading={`${crop.name} — questions and answers`} id="crop-faq" />

        {articles.length > 0 && (
          <section className="mt-14" aria-labelledby="crop-science-heading">
            <h2 id="crop-science-heading" className="font-display text-h3 font-semibold text-ink">
              The science behind this programme
            </h2>
            <ul className="mt-4 grid gap-3 md:grid-cols-3">
              {articles.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/knowledge/${a.slug}`}
                    className="block rounded-xl border border-sand-200 bg-white p-5 transition-colors hover:border-brand-400"
                  >
                    <span className="block font-semibold text-ink">{a.title}</span>
                    <span className="mt-1.5 block text-sm leading-relaxed text-ink-500">{a.summary}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-14 rounded-xl border border-sand-200 bg-sand-100/50 p-8 text-center">
          <h2 className="font-display text-h3 font-semibold text-ink">Not sure where to start?</h2>
          <p className="mx-auto mt-2 max-w-md text-ink-500">
            Tell us about your {crop.name.toLowerCase()} crop and our team will help you build the right programme.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button to="/crop-solutions" variant="outline" size="lg">
              Other crops
            </Button>
            <Button to={enquireTo} variant="primary" size="lg">
              Enquire now <Icon name="ArrowRight" className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
